import PropTypes from "prop-types";
import React, { useEffect, useState } from "react";
import { Card, CardBody, Col, Container, Row } from "reactstrap";
import { Line, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';

//Import Breadcrumb
import Breadcrumbs from "../../../components/Common/Breadcrumb";

//i18n
import { withTranslation } from "react-i18next";
import UserService from "Api/Services/UserService";
import DrugService from "Api/Services/DrugService";
import { Link, useNavigate } from "react-router-dom";
import PageTitle from "../../../components/Common/PageTitle";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const AdminDashboard = props => {
  document.title = "Admin Dashboard | Infuse One";
  const [totalDoctors, setTotalDoctors] = useState(0);
  const [appointmentStats, setAppointmentStats] = useState({
    total: 0,
    active: 0,
    completed: 0,
    cancelled: 0
  });
  const [patientStats, setPatientStats] = useState({
    total: 0,
    active: 0,
    completed: 0
  });
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const InfuseoneUserToken = localStorage.getItem("InfuseoneUserToken");
  const [isLoading, setLoading] = useState(false);

  const LoadDashboardMetrics = async () => {
    try { 
      setLoading(true);
      const response = await UserService.getDashboardMetrics();

      if (response.success && response.data) {
        const { data } = response;
        
        setTotalDoctors(data.totalDoctors || 0);
        
        setAppointmentStats({
          total: data.appointments.total || 0,
          active: data.appointments.active || 0,
          completed: data.appointments.completed || 0,
          cancelled: data.appointments.cancelled || 0
        });
        
        setPatientStats({
          total: data.patients.total || 0,
          active: data.patients.active || 0,
          completed: data.patients.completed || 0
        });
      } else {
        setError('Invalid response format from server');
      }
    } catch (error) {
      setError('Error loading dashboard metrics');
    } finally {
      setLoading(false);
    }
  };

  const reports = [
    { 
      title: "Total Doctors", 
      iconClass: "bx bx-user-plus font-size-24", 
      description: totalDoctors, 
      url: "/doctors",
      color: "primary"
    },
    { 
      title: "Total Appointments", 
      iconClass: "bx bx-calendar font-size-24",
      description: appointmentStats.total,
      subStats: [
        { label: "Active", value: appointmentStats.active, color: "warning" },
        { label: "Completed", value: appointmentStats.completed, color: "success" },
        { label: "Cancelled", value: appointmentStats.cancelled, color: "danger" }
      ],
      url: "/appointments",
      color: "success"
    },
    { 
      title: "Total Infuse One Patients", 
      iconClass: "bx bx-group font-size-24",
      description: patientStats.total,
      subStats: [
        { label: "Active", value: patientStats.active, color: "warning" },
        { label: "Completed", value: patientStats.completed, color: "success" }
      ],
      url: "/patients",
      color: "info"
    },
  ];

  const appointmentChartData = {
    labels: ['Active', 'Completed', 'Cancelled'],
    datasets: [
      {
        data: [
          appointmentStats.active,
          appointmentStats.completed,
          appointmentStats.cancelled,
        ],
        backgroundColor: [
          '#f1b44c', // warning
          '#34c38f', // success
          '#f46a6a', // danger
        ],
      },
    ],
  };

  const patientChartData = {
    labels: ['Active', 'Completed'],
    datasets: [
      {
        data: [
          patientStats.active,
          patientStats.completed,
        ],
        backgroundColor: [
          '#f1b44c', // warning
          '#34c38f', // success
        ],
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
  };

  useEffect(() => {
    setLoading(true)
    LoadDashboardMetrics();
  }, []);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="container-fluid">
            <PageTitle title="Admin Dashboard" />
            <Row>
              {reports.map((report, index) => (
                <Col md="4" key={"_col_" + index}>
                  <Card className="mini-stats-wid commonboxform" style={{ marginBottom: '1.5rem' }}>
                    <CardBody className="p-4">
                      <div className="d-flex">
                        <div className="flex-grow-1">
                          <p className="text-muted fw-medium mb-3 mt-4">
                            {report.title}
                          </p>
                          <h4 className="mb-3">{report.description || 0}</h4>
                          {report.subStats && (
                            <div className="mt-3 pt-2">
                              {report.subStats.map((stat, idx) => (
                                <div key={idx} className="d-flex align-items-center mb-2">
                                  <small className={`text-${stat.color} me-2`}>●</small>
                                  <small className="text-muted">{stat.label}:</small>
                                  <small className="ms-1 fw-medium">{stat.value}</small>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                        <div className={`avatar-sm rounded-circle bg-${report.color} align-self-center mini-stat-icon`}>
                          <span className={`avatar-title rounded-circle bg-${report.color}`}>
                            <i className={report.iconClass}></i>
                          </span>
                        </div>
                      </div>
                    </CardBody>
                  </Card>
                </Col>
              ))}
            </Row>

            <Row>
              <Col md="6">
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">Appointment Distribution</h4>
                    <div style={{ height: '300px' }}>
                      <Pie data={appointmentChartData} options={chartOptions} />
                    </div>
                  </CardBody>
                </Card>
              </Col>
              <Col md="6">
                <Card>
                  <CardBody>
                    <h4 className="card-title mb-4">Patient Distribution</h4>
                    <div style={{ height: '300px' }}>
                      <Pie data={patientChartData} options={chartOptions} />
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default withTranslation()(AdminDashboard);
